import React from 'react'
import { Link } from 'react-router-dom'
import {
  Box,
  Snackbar,
  Paper,
  Dialog,
  Button,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Theme,
  List,
  ListItem,
  Typography,
  TablePagination,
  TextField,
} from '@mui/material'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'
import { ToggleButton, ToggleButtonGroup } from '@mui/material'
import MuiAlert from '@mui/material/Alert'
import API from '@aws-amplify/api'
import { useSelector } from 'react-redux'
import { IReduxState } from 'interfaces/IReduxState'
import { isUserSuperuser } from 'shared/auth'
import {
  IStockCard,
  IStockCardBatch,
  StockCard,
  StockCardBatch,
  StockCardIngredients,
  StockCardPackaging,
  StockCardProducts,
} from 'interfaces/IStockCard'
import {
  STOCK_CARD_TYPE_COMPLETE,
  STOCK_CARD_TYPE_HALF_COMPLETE,
  STOCK_CARD_TYPE_INGREDIENTS,
  STOCK_CARD_TYPE_LABEL,
  STOCK_CARD_TYPE_PACKAGING,
  STOCK_CARD_TYPE_PREMIX,
  VALUE_BARANG_JADI,
  VALUE_GUDANG_BAHAN_BAKU,
  VALUE_GUDANG_JADI,
  VALUE_GUDANG_LABEL,
  VALUE_GUDANG_MIXING,
  VALUE_GUDANG_PACKAGING,
  VALUE_GUDANG_RUAHAN,
  VALUE_GUDANG_SETENGAH_JADI,
  VALUE_IN,
  VALUE_OUT,
  VALUE_SETENGAH_JADI,
} from 'enums/stockCard'
import loadingGif from '../../bobana stok.gif'
import LoadingIndicator from 'components/shared/LoadingIndicator'
import { IBatch } from 'interfaces/IPremix'
import { IProduct } from 'interfaces/IProduct'
// type-specific dialog components are in separate files
import PackagingDialog from './incomingStockCards/PackagingDialog'
import BatchDialog from './incomingStockCards/BatchDialog'
import HalfCompleteDialog from './incomingStockCards/HalfCompleteDialog'
import IngredientsDialog from './incomingStockCards/IngredientsDialog'
import InvoiceOutgoingDialog from './incomingStockCards/InvoiceOutgoingDialog'
import { IPackagingParts } from 'interfaces/IPackaging'
import { IIngredient } from 'interfaces/IIngredient'
import {
  formatStockQuantity,
  formatStockQuantityAuto,
} from 'components/inventory/helpers'
import getSourceOrDestinationLabel from 'shared/getSourceOrDestinationLabel'
import { Department } from 'enums/department'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    productRow: {
      cursor: 'pointer',
    },
  })
)

interface headCell {
  value: string
  label: string
  dataType: string
  isInOnly?: boolean
  isOutOnly?: boolean
}

interface IProductBatchOption {
  batchNumber: string
  productStock: number
  currentQuantity?: number
  expiryDate?: string | null
  productionDate?: string | null
}

export default function IncomingStockCards(props: {
  type: string
  inOutDirection: string
  qtyLabelIn?: string
  qtyLabelOut?: string
  noRestrictions?: number | boolean
  defaultRowsPerPage?: number
  department?: string
}) {
  const {
    type,
    inOutDirection,
    noRestrictions,
    defaultRowsPerPage = 100,
    department,
  } = props

  // Superuser detection
  const userGroups = useSelector((state: IReduxState) => state.userGroups || [])
  const isSuperuser = isUserSuperuser(userGroups)

  const headCells: { [key: string]: headCell[] } = {
    [VALUE_GUDANG_MIXING]: [
      { value: 'stockCardDisplayId', label: 'No. Dokumen', dataType: '' },
      {
        value: 'createdAt',
        label: 'Tanggal Bahan Baku Keluar',
        dataType: 'date',
      },
      {
        value: 'confirmedBy',
        label: 'Diterima Oleh',
        dataType: 'ingredientLevel',
      },
      { value: 'notes', label: 'Notes', dataType: '' },
    ],
    [VALUE_GUDANG_BAHAN_BAKU]: [
      { value: 'stockCardDisplayId', label: 'No. Dokumen', dataType: '' },
      {
        value: 'salesOrderDisplayId',
        label: 'No. SO',
        dataType: '',
        isOutOnly: true,
      },
      { value: 'outgoingDestination', label: 'Dari/Tujuan', dataType: '' },
      { value: 'batchNumber', label: 'No. Bets', dataType: '' },
      {
        value: 'createdAt',
        label: 'Tanggal Permintaan Dibuat',
        dataType: 'date',
      },
      { value: 'notes', label: 'Notes', dataType: '' },
    ],
    [VALUE_GUDANG_RUAHAN]: [
      { value: 'stockCardDisplayId', label: 'No. Dokumen', dataType: '' },
      {
        value: 'salesOrderDisplayId',
        label: 'No. SO',
        dataType: '',
        isOutOnly: true,
      },
      { value: 'premixId', label: 'Kode Ruahan', dataType: '' },
      { value: 'batchNumber', label: 'No. Bets', dataType: 'batchLevelData' },
      {
        value: 'createdAt',
        label: 'Tanggal Permintaan Dibuat',
        dataType: 'date',
      },
      {
        value: 'quantity',
        label: 'Quantity Yang Dimasukkan',
        dataType: 'batchLevelData',
        isInOnly: true,
      },
      {
        value: 'quantity',
        label: 'Quantity Yang Diminta',
        dataType: 'batchLevelData',
        isOutOnly: true,
      },
      { value: 'outgoingDestination', label: 'Dari/Tujuan', dataType: '' },
      {
        value: 'createdBy',
        label: 'Dibuat Oleh',
        dataType: '',
      },
      { value: 'notes', label: 'Notes', dataType: '' },
    ],
    [VALUE_GUDANG_PACKAGING]: [
      { value: 'stockCardDisplayId', label: 'No. Dokumen', dataType: '' },
      {
        value: 'salesOrderDisplayId',
        label: 'No. SO',
        dataType: '',
        isOutOnly: true,
      },
      { value: 'outgoingDestination', label: 'Dari/Tujuan', dataType: '' },
      {
        value: 'createdAt',
        label: 'Tanggal Permintaan Dibuat',
        dataType: 'date',
      },
      {
        value: 'createdBy',
        label: 'Dibuat Oleh',
        dataType: '',
      },
      { value: 'notes', label: 'Notes', dataType: '' },
    ],
    [VALUE_SETENGAH_JADI]: [
      { value: 'stockCardDisplayId', label: 'No. Dokumen', dataType: '' },
      {
        value: 'outgoingDestination',
        label: 'Dari',
        dataType: '',
        isInOnly: true,
      },
      {
        value: 'outgoingDestination',
        label: 'Tujuan',
        dataType: '',
        isOutOnly: true,
      },
      {
        value: 'createdAt',
        label: 'Tanggal Permintaan Dibuat',
        dataType: 'date',
      },
      {
        value: 'salesOrderDisplayId',
        label: 'No SO',
        dataType: '',
      },
      { value: 'productName', label: 'Nama Produk', dataType: '' },
      { value: 'brandId', label: 'Merek', dataType: '' },
      { value: 'premixId', label: 'Kode Ruahan', dataType: '' },
      { value: 'batchNumber', label: 'No. Bets', dataType: '' },
      { value: 'fillingAmount', label: 'Filling', dataType: '' },
      { value: 'packagingId', label: 'Kode Set Kemasan', dataType: '' },
      {
        value: 'quantity',
        label: 'Qty Diminta',
        dataType: '',
        isOutOnly: true,
      },
      {
        value: 'quantity',
        label: 'Qty Mau Dimasukkan',
        dataType: '',
        isInOnly: true,
      },
      {
        value: 'latestStock',
        label: inOutDirection === VALUE_IN ? 'Stok Sekarang' : 'Stok Sekarang',
        dataType: 'integer',
      },
      { value: 'notes', label: 'Notes', dataType: '' },
    ],
    [VALUE_BARANG_JADI]: [
      { value: 'stockCardDisplayId', label: 'No. Dokumen', dataType: '' },
      { value: 'productId', label: 'ID Produk', dataType: '' },
      { value: 'invoiceDisplayId', label: 'No. Invoice', dataType: '' },
      {
        value: 'invoiceCreatedAt',
        label: 'Tanggal Invoice Dibuat',
        dataType: 'date',
      },
      { value: 'salesOrderDisplayId', label: 'No. SO', dataType: '' },
      { value: 'productName', label: 'Nama Produk', dataType: '' },
      { value: 'packagingId', label: 'Kode Kemasan', dataType: '' },
      { value: 'packagingName', label: 'Nama Kemasan', dataType: '' },
      { value: 'pictureUrl', label: 'Gambar Kemasan', dataType: 'image' },
      { value: 'brandId', label: 'Merek', dataType: '' },
      { value: 'fillingAmount', label: 'Filling Amount', dataType: '' },
      { value: 'batchNumber', label: 'No. Bets', dataType: '' },
      { value: 'labelId', label: 'Label ID', dataType: '' },
      {
        value: 'createdAt',
        label: 'Tanggal Labeling Selesai',
        dataType: 'date',
      },
      {
        value: 'quantity',
        label: 'Quantity Yang Dimasukkan',
        dataType: '',
        isInOnly: true,
      },
      { value: 'notes', label: 'Notes', dataType: '' },
    ],
  }
  const [isSuccessSnackbarOpen, setIsSuccessSnackbarOpen] =
    React.useState(false)
  const [isFailureSnackbarOpen, setIsFailureSnackbarOpen] =
    React.useState(false)
  const [isWarningSnackbarOpen, setIsWarningSnackbarOpen] =
    React.useState(false)
  const classes = useStyles()

  const [batches, setBatches] = React.useState<IBatch[]>([])
  const [isBatchLoading, setIsBatchLoading] = React.useState(false)
  const [packagingParts, setPackagingParts] = React.useState<IPackagingParts[]>(
    []
  )
  const [ingredients, setIngredients] = React.useState<IIngredient[]>([])

  const [totalCount, setTotalCount] = React.useState(0)
  const [page, setPage] = React.useState(0)
  const [rowsPerPage, setRowsPerPage] = React.useState(defaultRowsPerPage)
  const [stockCards, setStockCards] = React.useState<IStockCard[]>([])
  const [stockCardData, setStockCardData] = React.useState<IStockCard>(
    new StockCard()
  )
  const [productBatchesByProductId, setProductBatchesByProductId] =
    React.useState<Record<string, IProductBatchOption[]>>({})
  const [isLoading, setIsLoading] = React.useState<any>(false)
  const [latestStock, setLatestStock] = React.useState<Array<number | null>>([])
  const [alertMessage, setAlertMessage] = React.useState('')
  const [stockCardSelected, setStockCardSelected] = React.useState(false)
  const [filterType, setFilterType] = React.useState('unconfirmed')
  const [isUpdating, setIsUpdating] = React.useState(false)
  const [isEditing, setIsEditing] = React.useState(false)

  const [searchDocNum, setSearchDocNum] = React.useState('')
  const [searchInvoiceNum, setSearchInvoiceNum] = React.useState('')
  const [searchSoNum, setSearchSoNum] = React.useState('')
  const [searchProductId, setSearchProductId] = React.useState('')

  // Superuser update function
  const updateStockCardOnServer = async () => {
    if (!stockCardData.stockCardId) return
    setIsUpdating(true)
    try {
      await API.patch(
        'products',
        `/premix/updateStockCard/${stockCardData.stockCardId}`,
        {
          body: {
            productId: stockCardData.productId,
            ingredientId: stockCardData.ingredientId,
            packagingId: stockCardData.packagingId,
            batchNumber: stockCardData.batchNumber,
            premixId: stockCardData.premixId,
            quantity: stockCardData.quantity,
            notes: stockCardData.notes,
            stockCardIngredients: stockCardData.stockCardIngredients,
            stockCardProducts: stockCardData.stockCardProducts,
            stockCardPackaging: stockCardData.stockCardPackaging,
            stockCardBatches: stockCardData.stockCardBatches,
            outgoingDestination: stockCardData.outgoingDestination,
            salesOrderDisplayId: stockCardData.salesOrderDisplayId,
            purchaseOrderNumber: stockCardData.purchaseOrderNumber,
          },
        }
      )
      setAlertMessage(`successfully edited ${stockCardData.stockCardDisplayId}`)
      setIsSuccessSnackbarOpen(true)
      handleDialogClose()
      loadStockCard()
    } catch (error) {
      console.error(error)
      setAlertMessage('Gagal mengupdate kartu stok.')
      setIsFailureSnackbarOpen(true)
    } finally {
      setIsUpdating(false)
    }
  }

  const handleFilterChange = (
    event: React.MouseEvent<HTMLElement>,
    newFilter: string | null
  ) => {
    if (newFilter !== null) {
      setFilterType(newFilter)
      setPage(0)
    }
  }

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleSnackbarClose = (): void => {
    setIsFailureSnackbarOpen(false)
    setIsSuccessSnackbarOpen(false)
    setIsWarningSnackbarOpen(false)
  }

  const getCurrentHalfCompleteStock = async (stockCard: IStockCard) => {
    if (!stockCard.stockCardProducts?.length) {
      setLatestStock([])
      return
    }

    setIsBatchLoading(true)
    try {
      const products: Array<number | null> = await Promise.all(
        stockCard.stockCardProducts.map(async (product) => {
          const normalizedProductId =
            product.productId || product.product?.productId

          if (!normalizedProductId) {
            return (
              (product.product as any)?.inventory ??
              (product.product as any)?.stock ??
              (product as any)?.latestStock ??
              null
            )
          }

          try {
            const batchNumber =
              product.batchNumber || product.product?.batchNumber
            if (batchNumber) {
              // Fetch batch-specific product stock from tblProductBatch
              const batches = await API.get(
                'products',
                `/products/getProductBatches`,
                {
                  queryStringParameters: {
                    productId: String(normalizedProductId),
                  },
                }
              )
              if (Array.isArray(batches)) {
                const matchingProductBatch = batches.find(
                  (b: any) =>
                    b.batchNumber?.toString().toLowerCase() ===
                    batchNumber.toString().toLowerCase()
                )
                if (
                  matchingProductBatch?.productStock !== undefined &&
                  matchingProductBatch?.productStock !== null
                ) {
                  return matchingProductBatch.productStock as number
                }
              }
            }

            // Fall back to total product inventory (sum of all batch stocks or pr.inventory)
            const productData = await API.get(
              'products',
              `/products/getProduct/${normalizedProductId}`,
              {}
            )
            const resolved = Array.isArray(productData)
              ? productData[0]
              : productData
            return (resolved?.inventory ?? resolved?.stock ?? null) as
              | number
              | null
          } catch (error) {
            console.log(
              'Failed to fetch product inventory for stock card product',
              normalizedProductId,
              error
            )
            return (
              (product.product as any)?.inventory ??
              (product.product as any)?.stock ??
              (product as any)?.latestStock ??
              null
            )
          }
        })
      )

      setLatestStock(products)
      if (products.length === 1 && products[0] !== null) {
        setStockCardData((prev) => ({ ...prev, latestStock: products[0] }))
      }
    } finally {
      setIsBatchLoading(false)
    }
  }

  const getStockCardProductId = (
    product: {
      productId?: number | string | null
      product?: IProduct | null
    },
    stockCard: IStockCard = stockCardData
  ) =>
    product.productId ||
    product.product?.productId ||
    (stockCard.stockCardProducts?.length === 1 ? stockCard.productId : null) ||
    null

  const getProductBatchOptions = (product: {
    productId?: number | string | null
    product?: IProduct | null
    batchNumber?: string | null
    latestStock?: number | string | null
  }): IProductBatchOption[] => {
    const productId = getStockCardProductId(product)
    if (!productId) return []

    const selectedBatchNumber = String(product.batchNumber || '').trim()
    const normalizedSelectedBatchNumber = selectedBatchNumber.toLowerCase()

    const options = productBatchesByProductId[String(productId)] || []
    const filteredOptions = options.filter((batch) => {
      const batchNumber = String(batch.batchNumber || '')
        .trim()
        .toLowerCase()
      return (
        Number(batch.productStock || 0) > 0 ||
        (!!normalizedSelectedBatchNumber &&
          batchNumber === normalizedSelectedBatchNumber)
      )
    })

    if (
      selectedBatchNumber &&
      !filteredOptions.some(
        (batch) =>
          String(batch.batchNumber || '')
            .trim()
            .toLowerCase() === normalizedSelectedBatchNumber
      )
    ) {
      filteredOptions.unshift({
        batchNumber: selectedBatchNumber,
        productStock: Number(product.latestStock ?? 0),
      })
    }

    return filteredOptions
  }

  const loadProductBatchOptions = async (stockCard: IStockCard) => {
    const productIds = Array.from(
      new Set(
        (stockCard.stockCardProducts || [])
          .map((product) => getStockCardProductId(product, stockCard))
          .filter((productId): productId is string | number => !!productId)
          .map((productId) => String(productId))
      )
    )

    if (productIds.length === 0) return

    try {
      const entries = await Promise.all(
        productIds.map(async (productId) => {
          const response = await API.get(
            'products',
            '/products/getProductBatches',
            {
              queryStringParameters: { productId },
            }
          )
          return [productId, Array.isArray(response) ? response : []] as const
        })
      )

      setProductBatchesByProductId((prev) => ({
        ...prev,
        ...Object.fromEntries(entries),
      }))
    } catch (error) {
      console.error('Failed to load product batches', error)
    }
  }

  const getCurrentPackagingStock = async (stockCard: IStockCard) => {
    const packagingParts = await Promise.all(
      stockCard.stockCardPackaging.map(async (packaging) => {
        const response = await API.get(
          'products',
          `/packaging/getPackagingPart/${packaging.packagingPartId}`,
          {}
        )
        // Handle both { data, count } and [...] formats
        if (response?.data && Array.isArray(response.data)) {
          return response.data[0]
        }
        return Array.isArray(response) ? response[0] : response
      })
    )

    setPackagingParts(packagingParts.filter(Boolean))
  }

  const getCurrentIngredientStock = async (stockCard: IStockCard) => {
    const ingredientIds = stockCard.stockCardIngredients.map(
      (stockCardIngredient) =>
        stockCardIngredient.ingredientId ||
        stockCardIngredient.ingredient?.ingredientId
    )
    const ingredientPromises = ingredientIds.map((ingredientId) =>
      API.get('products', `/ingredients/getIngredient/${ingredientId}`, {})
    )

    const ingredients = await Promise.all(ingredientPromises)
    setIngredients(ingredients.map((ingredient) => ingredient[0]))
  }

  const handleStockCardFieldChange = (
    index: number,
    value: any,
    key: string
  ): void => {
    const sanitizeNumericInput = (input: any) => {
      if (input == null) return input
      let s = String(input)
      s = s.replace(/[^0-9.-]/g, '')
      const parts = s.split('.')
      if (parts.length > 2) s = parts.shift() + '.' + parts.join('')
      s = s.replace(/(?!^)-/g, '')
      return s
    }

    const shouldSanitize = /quantity|qty|amount|confirmed|stock|id/i.test(key)
    const newValue = shouldSanitize ? sanitizeNumericInput(value) : value

    setStockCardData((prev) => ({
      ...prev,
      stockCardProducts: prev.stockCardProducts.map((product, idx) => {
        if (idx === index) {
          if (key === 'latestStock') {
            const newLatestStock = [...latestStock]
            newLatestStock[idx] = newValue === '' ? null : Number(newValue)
            setLatestStock(newLatestStock)
          }

          if (key === 'batchNumber') {
            const matchingBatch = getProductBatchOptions(product).find(
              (batch) =>
                String(batch.batchNumber || '').toLowerCase() ===
                String(newValue || '').toLowerCase()
            )
            const newLatestStock = [...latestStock]
            newLatestStock[idx] =
              matchingBatch?.productStock == null
                ? null
                : Number(matchingBatch.productStock)
            setLatestStock(newLatestStock)
            return {
              ...product,
              batchNumber: newValue,
              latestStock: newLatestStock[idx],
            }
          }

          if (key === 'product') {
            const p = value as IProduct
            if (p.productId) {
              loadProductBatchOptions({
                ...prev,
                stockCardProducts: [{ ...product, productId: p.productId }],
              })
            }
            return {
              ...product,
              productId: p.productId,
              premixId: p.premixId,
              packagingId: p.packagingId,
              fillingAmount: String(p.weight),
              fillingUnit: String(p.unit),
            }
          }
          return { ...product, [key]: newValue }
        }
        return product
      }),
    }))
  }
  const handleStockCardBatchFieldChange = (
    value: any,
    key: string,
    incomingStockCardBatch: IStockCardBatch
  ): void => {
    const index = stockCardData.stockCardBatches.indexOf(incomingStockCardBatch)
    if (index === -1) return

    const sanitizeNumericInput = (input: any) => {
      if (input == null) return input
      let s = String(input)
      s = s.replace(/[^0-9.-]/g, '')
      const parts = s.split('.')
      if (parts.length > 2) s = parts.shift() + '.' + parts.join('')
      s = s.replace(/(?!^)-/g, '')
      return s
    }

    const shouldSanitize = /quantity|qty|amount|confirmed|stock|id/i.test(key)
    const newValue = shouldSanitize ? sanitizeNumericInput(value) : value

    const newBatches = [...stockCardData.stockCardBatches]
    newBatches[index] = { ...incomingStockCardBatch, [key]: newValue }

    setStockCardData({ ...stockCardData, stockCardBatches: newBatches })
  }

  const getMatchingBatch = (item: { batchNumber?: string | null }) => {
    const matchingBatches = batches.filter((batch) => {
      const b1 = (batch.batchNumber || '').toString().trim().toLowerCase()
      const b2 = (item.batchNumber || '').toString().trim().toLowerCase()
      return b1 === b2 && b1 !== ''
    })

    if (matchingBatches.length <= 1) return matchingBatches[0]

    // If multiple entries exist, prioritize the physical one (isVirtual: 0)
    return matchingBatches.find((b) => !b.isVirtual) || matchingBatches[0]
  }

  const isValidNumberString = (val: any) => {
    if (val == null) return false
    const s = String(val).trim()
    return /^-?\d+(\.\d+)?$/.test(s)
  }

  const handleStockCardIngredientFieldChange = (
    ingredientIdentifier: string,
    value: any,
    key: string
  ): void => {
    let updated = false
    const normalizedIngredientIdentifier = ingredientIdentifier ?? ''
    const sanitizeNumericInput = (input: any) => {
      if (input == null) return input
      let s = String(input)
      s = s.replace(/[^0-9.-]/g, '')
      const parts = s.split('.')
      if (parts.length > 2) s = parts.shift() + '.' + parts.join('')
      s = s.replace(/(?!^)-/g, '')
      return s
    }

    const shouldSanitize = /quantity|qty|amount|confirmed|stock|id/i.test(key)

    setStockCardData((prevData) => ({
      ...prevData,
      stockCardIngredients: prevData.stockCardIngredients.map(
        (stockCardIngredient, index) => {
          const matchesStockCardIngredientId =
            normalizedIngredientIdentifier.startsWith('stockCardIngredientId:') &&
            String(stockCardIngredient.stockCardIngredientId) ===
              normalizedIngredientIdentifier.replace('stockCardIngredientId:', '')
          const matchesStockCardIngredientIndex =
            normalizedIngredientIdentifier.startsWith('stockCardIngredientIndex:') &&
            String(index) ===
              normalizedIngredientIdentifier.replace('stockCardIngredientIndex:', '')
          const matchesLegacyIngredientId =
            normalizedIngredientIdentifier &&
            !normalizedIngredientIdentifier.startsWith('stockCardIngredientId:') &&
            !normalizedIngredientIdentifier.startsWith('stockCardIngredientIndex:') &&
            stockCardIngredient.ingredientId === normalizedIngredientIdentifier

          if (
            matchesStockCardIngredientId ||
            matchesStockCardIngredientIndex ||
            matchesLegacyIngredientId
          ) {
            if (key === 'ingredient') {
              const ing = value as any // IIngredient
              return {
                ...stockCardIngredient,
                ingredientId: ing.ingredientId,
                ingredientName: ing.ingredientName,
              }
            }
            return {
              ...stockCardIngredient,
              [key]: shouldSanitize ? sanitizeNumericInput(value) : value,
            }
          } else if (
            !normalizedIngredientIdentifier &&
            !stockCardIngredient.ingredientId &&
            !updated
          ) {
            updated = true
            if (key === 'ingredient') {
              const ing = value as any // IIngredient
              return {
                ...stockCardIngredient,
                ingredientId: ing.ingredientId,
                ingredientName: ing.ingredientName,
              }
            }
            return {
              ...stockCardIngredient,
              [key]: shouldSanitize ? sanitizeNumericInput(value) : value,
            }
          } else {
            return stockCardIngredient
          }
        }
      ),
    }))
  }

  const handleStockCardPackagingFieldChange = (
    packagingPartId: string,
    value: any,
    key: string
  ): void => {
    const sanitizeNumericInput = (input: any) => {
      if (input == null) return input
      let s = String(input)
      s = s.replace(/[^0-9.-]/g, '')
      const parts = s.split('.')
      if (parts.length > 2) s = parts.shift() + '.' + parts.join('')
      s = s.replace(/(?!^)-/g, '')
      return s
    }

    const shouldSanitize = /quantity|qty|amount|confirmed|stock|id/i.test(key)
    const newValue = shouldSanitize ? sanitizeNumericInput(value) : value

    setStockCardData({
      ...stockCardData,
      stockCardPackaging: stockCardData.stockCardPackaging.map(
        (stockCardPackaging) => {
          if (stockCardPackaging.packagingPartId === packagingPartId) {
            if (key === 'packagingPart') {
              const part = value as any // IPackagingParts
              return {
                ...stockCardPackaging,
                packagingPartId: part.packagingPartId,
                partName: part.partName,
              }
            }
            return { ...stockCardPackaging, [key]: newValue }
          } else {
            return stockCardPackaging
          }
        }
      ),
    })
  }

  const handleDialogClose = () => {
    setStockCardSelected(false)
    setIsEditing(false)
    setStockCardData(new StockCard())
  }

  const loadStockCard = React.useCallback(async () => {
    let stockCardType = ''
    switch (type) {
      case VALUE_GUDANG_MIXING:
        stockCardType = STOCK_CARD_TYPE_INGREDIENTS
        break
      case VALUE_GUDANG_BAHAN_BAKU:
        stockCardType = STOCK_CARD_TYPE_INGREDIENTS
        break
      case VALUE_GUDANG_PACKAGING:
        stockCardType = STOCK_CARD_TYPE_PACKAGING
        break
      case VALUE_GUDANG_RUAHAN:
        stockCardType = STOCK_CARD_TYPE_PREMIX
        break
      case VALUE_GUDANG_LABEL:
        stockCardType = STOCK_CARD_TYPE_LABEL
        break
      case VALUE_GUDANG_SETENGAH_JADI:
      case VALUE_SETENGAH_JADI:
        stockCardType = STOCK_CARD_TYPE_HALF_COMPLETE
        break
      case VALUE_BARANG_JADI:
      case VALUE_GUDANG_JADI:
        stockCardType = STOCK_CARD_TYPE_COMPLETE
        break
    }

    let isConfirmedParam: string | boolean = false
    if (filterType === 'all') {
      isConfirmedParam = 'ALL'
    } else if (filterType === 'confirmed') {
      isConfirmedParam = true
    } else {
      isConfirmedParam = false
    }

    const param = {
      queryStringParameters: {
        isConfirmed: isConfirmedParam,
        inOutDirection,
        type: stockCardType,
        pageSize: rowsPerPage,
        page: page + 1,
        ...(noRestrictions !== undefined && { noRestrictions }),
        department: department,
      },
    }

    setIsLoading(true)
    try {
      try {
        const stockCards: { stockCards: IStockCard[]; count: number } =
          await API.get('products', `/premix/getStockCard`, param)
        if (!stockCards?.stockCards?.length) {
          setAlertMessage('Tidak ada permintaan konfirmasi stok.')
          setIsWarningSnackbarOpen(true)
          setStockCards([])
          setTotalCount(0)
        } else {
          setStockCards(
            stockCards?.stockCards
              ?.map((stockCard: IStockCard) => new StockCard(stockCard))
              .sort(
                (a, b) =>
                  new Date(b.createdAt ?? 0).getTime() -
                  new Date(a.createdAt ?? 0).getTime()
              )
          )
          setTotalCount(stockCards.count)
        }
      } catch (error) {
        setAlertMessage('Gagal memuat kartu stok.')
        setIsFailureSnackbarOpen(true)
        console.error(error)
      }
    } finally {
      setIsLoading(false)
    }
  }, [
    type,
    inOutDirection,
    setTotalCount,
    filterType,
    page,
    rowsPerPage,
    noRestrictions,
    department,
  ])

  const getBatchData = async (stockCard: IStockCard) => {
    setIsBatchLoading(true)
    try {
      const premixId =
        stockCard.premixId ||
        stockCard.stockCardBatches?.[0]?.batch?.premixId ||
        stockCard.stockCardProducts?.[0]?.premixId

      const batchNumber =
        stockCard.batchNumber ||
        stockCard.stockCardBatches?.[0]?.batchNumber ||
        stockCard.stockCardBatches?.[0]?.batch?.batchNumber ||
        stockCard.stockCardProducts?.[0]?.batchNumber

      let url = '/premix/getBatches'
      const params: any = {
        showInactive: true,
      }

      if (premixId) {
        params.premixId = premixId.toString().trim()
        params.pageSize = 999999
      } else if (batchNumber) {
        url = `/premix/getBatches/${batchNumber.toString().trim()}`
      } else {
        params.pageSize = 999999
      }

      console.log('Fetching batches from:', url, 'with params:', params)

      const response = await API.get('products', url, {
        queryStringParameters: params,
      })

      const resolvedBatches =
        response?.batches ||
        response?.data ||
        (Array.isArray(response) ? response : [response]).filter(Boolean)
      console.log('API Response (batches):', resolvedBatches)
      setBatches(resolvedBatches)
    } catch (error) {
      console.error('Failed to load batches', error)
      setBatches([])
    } finally {
      setIsBatchLoading(false)
    }
  }

  const handleStockCardSelection = (stockCardId: number) => {
    const matchedStockCard = stockCards.find(
      (stockCard) => stockCard.stockCardId === stockCardId
    )

    if (!matchedStockCard) {
      return
    }
    setStockCardData(matchedStockCard)
    setStockCardSelected(true)
    setIsEditing(false)
    setLatestStock([])
    setProductBatchesByProductId({})

    if (
      (matchedStockCard.stockCardBatches?.length ?? 0) > 0 ||
      (matchedStockCard.stockCardProducts?.length ?? 0) > 0 ||
      matchedStockCard.premixId
    ) {
      getBatchData(matchedStockCard)
    }

    if (matchedStockCard.stockCardProducts?.length > 0) {
      getCurrentHalfCompleteStock(matchedStockCard)
      loadProductBatchOptions(matchedStockCard)
    }

    if (matchedStockCard.stockCardIngredients?.length > 0) {
      getCurrentIngredientStock(matchedStockCard)
    }

    if (matchedStockCard.stockCardPackaging?.length > 0) {
      getCurrentPackagingStock(matchedStockCard)
    }
  }

  const confirmStockCardIngredient = () => {
    const stockCardId = stockCardData.stockCardId
    setIsLoading(true)
    API.post('products', `/premix/confirmStockCardIngredient/${stockCardId}`, {
      body: stockCardData.stockCardIngredients.map((stockCardIngredient) => ({
        ...stockCardIngredient,
        confirmedQuantity:
          inOutDirection === VALUE_OUT
            ? -(stockCardIngredient.confirmedQuantity ?? 0)
            : stockCardIngredient.confirmedQuantity,
      })),
    })
      .then((response) => {
        setAlertMessage(
          `Berhasil menandai stok ${
            inOutDirection === VALUE_IN ? 'diterima' : 'dikeluarkan'
          }.`
        )
        setIsSuccessSnackbarOpen(true)
        setStockCardSelected(false)
        loadStockCard()
      })
      .catch((error) => {
        console.error(error)
        setAlertMessage(
          `Gagal menandai stok ${
            inOutDirection === VALUE_IN ? 'diterima' : 'dikeluarkan'
          }.`
        )
        setIsFailureSnackbarOpen(true)
        console.error(error.response)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const confirmBarangSetengahJadi = () => {
    const stockCardId = stockCardData.stockCardId
    setIsLoading(true)
    API.post('products', `/premix/confirmStockCardProduct/${stockCardId}`, {
      body: {
        ...stockCardData,
      },
    })
      .then((response) => {
        setAlertMessage(
          `Berhasil menandai stok ${
            inOutDirection === VALUE_IN ? 'diterima' : 'dikeluarkan'
          }.`
        )
        setIsSuccessSnackbarOpen(true)
        setStockCardSelected(false)
        loadStockCard()
      })
      .catch((error) => {
        console.error(error)
        setAlertMessage(
          `Gagal menandai stok ${
            inOutDirection === VALUE_IN ? 'diterima' : 'dikeluarkan'
          }.`
        )
        setIsFailureSnackbarOpen(true)
        console.error(error.response)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const confirmPackaging = () => {
    const stockCardId = stockCardData.stockCardId
    setIsLoading(true)
    API.post('products', `/premix/confirmStockCardPackaging/${stockCardId}`, {
      body: stockCardData.stockCardPackaging.map((stockCardPackaging) => ({
        ...stockCardPackaging,
        confirmedQuantity:
          inOutDirection === VALUE_OUT
            ? -(stockCardPackaging.confirmedQuantity ?? 0)
            : stockCardPackaging.confirmedQuantity,
      })),
    })
      .then((response) => {
        setAlertMessage(
          `Berhasil menandai stok ${
            inOutDirection === VALUE_IN ? 'diterima' : 'dikeluarkan'
          }.`
        )
        loadStockCard()
        setIsSuccessSnackbarOpen(true)
        setStockCardSelected(false)
      })
      .catch((error) => {
        console.error(error)
        setAlertMessage(
          `Gagal menandai stok ${
            inOutDirection === VALUE_IN ? 'diterima' : 'dikeluarkan'
          }.`
        )
        setIsFailureSnackbarOpen(true)
        console.error(error.response)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const handleAddIngredient = () => {
    setStockCardData({
      ...stockCardData,
      stockCardIngredients: [
        ...stockCardData.stockCardIngredients,
        new StockCardIngredients({
          stockCardId: stockCardData.stockCardId ?? undefined,
        }),
      ],
    })
  }

  const handleRemoveIngredient = (index: number) => {
    const newIngredients = [...stockCardData.stockCardIngredients]
    newIngredients.splice(index, 1)
    setStockCardData({
      ...stockCardData,
      stockCardIngredients: newIngredients,
    })
  }

  const handleAddBatch = () => {
    setStockCardData({
      ...stockCardData,
      stockCardBatches: [
        ...stockCardData.stockCardBatches,
        new StockCardBatch({
          stockCardId: stockCardData.stockCardId ?? undefined,
        }),
      ],
    })
  }

  const handleRemoveBatch = (index: number) => {
    const newBatches = [...stockCardData.stockCardBatches]
    newBatches.splice(index, 1)
    setStockCardData({
      ...stockCardData,
      stockCardBatches: newBatches,
    })
  }

  const handleAddProduct = () => {
    setStockCardData({
      ...stockCardData,
      stockCardProducts: [
        ...stockCardData.stockCardProducts,
        new StockCardProducts({
          stockCardId: stockCardData.stockCardId ?? undefined,
        }),
      ],
    })
  }

  const handleRemoveProduct = (index: number) => {
    const newProducts = [...stockCardData.stockCardProducts]
    newProducts.splice(index, 1)
    setStockCardData({
      ...stockCardData,
      stockCardProducts: newProducts,
    })
  }

  const handleAddPackaging = () => {
    setStockCardData({
      ...stockCardData,
      stockCardPackaging: [
        ...stockCardData.stockCardPackaging,
        new StockCardPackaging({
          stockCardId: stockCardData.stockCardId ?? undefined,
        }),
      ],
    })
  }

  const handleRemovePackaging = (index: number) => {
    const newPackaging = [...stockCardData.stockCardPackaging]
    newPackaging.splice(index, 1)
    setStockCardData({
      ...stockCardData,
      stockCardPackaging: newPackaging,
    })
  }

  const confirmBatch = () => {
    const stockCardId = stockCardData.stockCardId
    setIsLoading(true)
    API.post('products', `/premix/confirmStockCardBatch/${stockCardId}`, {
      body: stockCardData.stockCardBatches.map((stockCardBatch) => ({
        ...stockCardBatch,
        confirmedQuantity:
          inOutDirection === VALUE_OUT
            ? -(stockCardBatch.confirmedQuantity ?? 0)
            : stockCardBatch.confirmedQuantity,
      })),
    })
      .then((response) => {
        setAlertMessage(
          `Berhasil menandai stok ${
            inOutDirection === VALUE_IN ? 'diterima' : 'dikeluarkan'
          }.`
        )
        loadStockCard()
        setIsSuccessSnackbarOpen(true)
        setStockCardSelected(false)
      })
      .catch((error) => {
        console.error(error)
        setAlertMessage(
          `Gagal menandai stok ${
            inOutDirection === VALUE_IN ? 'diterima' : 'dikeluarkan'
          }.`
        )
        setIsFailureSnackbarOpen(true)
        console.error(error.response)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  // run when loadStockCard changes (it is memoized)
  React.useEffect(() => {
    loadStockCard()
  }, [loadStockCard])

  const isKosmetikComplete =
    type === VALUE_BARANG_JADI && department === Department.KOSMETIK
  const formatQty = isKosmetikComplete
    ? formatStockQuantityAuto
    : formatStockQuantity

  const mapData = (stockCard: IStockCard, headCell: headCell) => {
    // Show salesOrderDisplayId for gudang barang jadi
    if (headCell.value === 'salesOrderDisplayId') {
      return stockCard.salesOrderDisplayId || '-'
    }
    // Show salesOrderCreatedAt for gudang barang jadi
    if (headCell.value === 'salesOrderCreatedAt') {
      const value = stockCard.salesOrderCreatedAt
      return value ? new Date(value).toLocaleDateString('id-ID') : '-'
    }
    if (headCell.value === 'invoiceDisplayId') {
      return stockCard.invoiceDisplayId || '-'
    }
    if (headCell.value === 'invoiceCreatedAt') {
      const value = stockCard.invoiceCreatedAt
      return value ? new Date(value).toLocaleDateString('id-ID') : '-'
    }
    if (headCell.value === 'outgoingDestination') {
      const destination = getSourceOrDestinationLabel(
        stockCard.outgoingDestination
      )
      if (stockCard.invoiceId) {
        return (
          <Link
            to="/invoice/manage"
            style={{ color: '#1976d2', textDecoration: 'none', fontWeight: 600 }}
          >
            {destination}
          </Link>
        )
      }
      return destination
    }
    if (headCell.value === 'productId') {
      const products = stockCard.stockCardProducts || []
      if (products.length > 1) {
        return '(Multiple)'
      }
      const prod = products[0]
      return prod?.productId || prod?.product?.productId || stockCard.productId || '-'
    }
    if (headCell.value === 'productName') {
      const products = stockCard.stockCardProducts || []
      if (products.length > 1) {
        return '(Multiple Products)'
      }
      const prod = products[0]
      return prod?.productName || prod?.product?.productName || '-'
    }
    // Show image for Gambar Kemasan column
    if (headCell.value === 'pictureUrl' && headCell.dataType === 'image') {
      const url = stockCard.pictureUrl
      const isString = typeof url === 'string'
      return isString && url ? (
        <img
          src={url as string}
          alt="Gambar Kemasan"
          style={{
            maxWidth: 80,
            maxHeight: 80,
            objectFit: 'contain',
            border: '1px solid #ccc',
            borderRadius: 8,
          }}
        />
      ) : (
        <span style={{ color: '#888', fontStyle: 'italic' }}>
          Tidak ada gambar
        </span>
      )
    }
    const singleStockCardProduct =
      stockCard.stockCardProducts?.length === 1
        ? stockCard.stockCardProducts[0]
        : null

    if (singleStockCardProduct && headCell.value === 'quantity') {
      const val = singleStockCardProduct.quantity
      if (Number(val) > 0) return formatQty(val)
      return formatQty(
        singleStockCardProduct.expectedQuantity ||
          // @ts-ignore
          stockCard[headCell.value] ||
          0
      )
    }

    if (
      singleStockCardProduct &&
      ['premixId', 'packagingId', 'brandId', 'batchNumber', 'labelId'].includes(
        headCell.value
      )
    ) {
      return (
        // @ts-ignore
        singleStockCardProduct[headCell.value] ||
        singleStockCardProduct.product?.[headCell.value] ||
        stockCard[headCell.value]
      )
    }

    if (headCell.value === 'packagingName') {
      return (
        stockCard.packagingName ||
        singleStockCardProduct?.product?.packagingName ||
        '-'
      )
    }

    if (singleStockCardProduct && headCell.value === 'fillingAmount') {
      const value =
        singleStockCardProduct.fillingAmount ??
        singleStockCardProduct.product?.fillingAmount ??
        // @ts-ignore
        stockCard[headCell.value]
      const unit = singleStockCardProduct.fillingUnit ?? ''
      if (value === null || value === undefined || value === '') {
        return '-'
      }
      return `${value}${unit}`
    }

    if (headCell.dataType === 'date') {
      const value = stockCard[headCell.value]
      if (typeof value === 'string' || typeof value === 'number') {
        return value ? new Date(value).toDateString() : '-'
      }
      return '-'
    }
    if (headCell.dataType === 'ingredientLevel') {
      // @ts-ignore
      const value = stockCard.stockCardIngredients?.[0]?.[headCell.value]
      return value
    }
    if (headCell.dataType === 'ingredientLevelDate') {
      // @ts-ignore
      const value = stockCard.stockCardIngredients?.[0]?.[headCell.value]
      if (typeof value === 'string' || typeof value === 'number') {
        return new Date(value).toUTCString()
      }
      return '-'
    }
    if (headCell.dataType === 'batchLevelData') {
      // @ts-ignore
      const value = stockCard.stockCardBatches?.[0]?.[headCell.value]
      if (headCell.value === 'quantity') return formatQty(value ?? 0)
      return value
    }
    if (headCell.dataType === 'productLevelDateData') {
      // @ts-ignore
      const value = stockCard.stockCardProducts?.[0]?.product?.[headCell.value]
      if (typeof value === 'string' || typeof value === 'number') {
        return value ? new Date(value).toDateString() : '-'
      }
      return '-'
    }
    if (headCell.dataType === 'stockCardProductLevelData') {
      // @ts-ignore
      const value = stockCard.stockCardProducts?.[0]?.[headCell.value]
      return value
    }
    if (headCell.dataType === 'integer') {
      // For latestStock on half-complete/complete products, get from product inventory
      if (
        headCell.value === 'latestStock' &&
        (stockCard.type === STOCK_CARD_TYPE_HALF_COMPLETE ||
          stockCard.type === STOCK_CARD_TYPE_COMPLETE) &&
        stockCard.stockCardProducts?.length
      ) {
        if (stockCard.stockCardProducts.length > 1) {
          return '(Multiple)'
        }
        const productInventory =
          stockCard.stockCardProducts[0]?.product?.inventory ??
          stockCard.stockCardProducts[0]?.product?.stock ??
          stockCard.stockCardProducts[0]?.latestStock ??
          stockCard.latestStock
        return formatQty(productInventory ?? 0)
      }
      if (
        headCell.value === 'latestStock' &&
        stockCard.type === STOCK_CARD_TYPE_PREMIX &&
        stockCard.stockCardBatches?.length
      ) {
        if (stockCard.stockCardBatches.length > 1) {
          return '(Multiple)'
        }
        const matchingBatch = getMatchingBatch(stockCard.stockCardBatches[0])
        return formatQty(matchingBatch?.currentQuantity ?? 0)
      }
      return formatQty(stockCard[headCell.value] ?? 0)
    }
    return stockCard[headCell.value]
  }

  const escapeHtml = (value: any) =>
    String(value ?? '-')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')

  const getPrintValue = (stockCard: IStockCard, headCell: headCell) => {
    if (headCell.value === 'salesOrderDisplayId') {
      return { type: 'text', value: stockCard.salesOrderDisplayId || '-' }
    }
    if (headCell.value === 'salesOrderCreatedAt') {
      const value = stockCard.salesOrderCreatedAt
      return {
        type: 'text',
        value: value ? new Date(value).toLocaleDateString('id-ID') : '-',
      }
    }
    if (headCell.value === 'invoiceDisplayId') {
      return { type: 'text', value: stockCard.invoiceDisplayId || '-' }
    }
    if (headCell.value === 'invoiceCreatedAt') {
      const value = stockCard.invoiceCreatedAt
      return {
        type: 'text',
        value: value ? new Date(value).toLocaleDateString('id-ID') : '-',
      }
    }
    if (headCell.value === 'outgoingDestination') {
      return {
        type: 'text',
        value: getSourceOrDestinationLabel(stockCard.outgoingDestination),
      }
    }
    if (headCell.value === 'productName') {
      const products = stockCard.stockCardProducts || []
      if (products.length > 1) {
        return { type: 'text', value: '(Multiple Products)' }
      }
      const prod = products[0]
      return {
        type: 'text',
        value: prod?.productName || prod?.product?.productName || '-',
      }
    }
    if (headCell.value === 'pictureUrl' && headCell.dataType === 'image') {
      const url = stockCard.pictureUrl
      return {
        type: 'image',
        value: typeof url === 'string' && url ? url : '',
      }
    }

    const isMultiProduct = (stockCard.stockCardProducts?.length || 0) > 1
    const singleStockCardProduct = !isMultiProduct
      ? stockCard.stockCardProducts?.[0]
      : null

    if (
      singleStockCardProduct &&
      ['premixId', 'packagingId', 'brandId', 'batchNumber', 'labelId'].includes(
        headCell.value
      )
    ) {
      const value =
        // @ts-ignore
        singleStockCardProduct[headCell.value] ||
        singleStockCardProduct.product?.[headCell.value] ||
        stockCard[headCell.value]
      return { type: 'text', value: value ?? '-' }
    }

    if (
      isMultiProduct &&
      ['premixId', 'packagingId', 'brandId', 'batchNumber', 'labelId'].includes(
        headCell.value
      )
    ) {
      const vals = stockCard.stockCardProducts.map(
        (p) =>
          p[headCell.value] ||
          p.product?.[headCell.value] ||
          stockCard[headCell.value]
      )
      const unique = Array.from(new Set(vals.filter((v) => !!v)))
      return {
        type: 'text',
        value: unique.length === 1 ? unique[0] : '(Multiple)',
      }
    }

    if (headCell.value === 'packagingName') {
      const value =
        stockCard.packagingName ||
        singleStockCardProduct?.product?.packagingName ||
        '-'
      return { type: 'text', value }
    }

    if (singleStockCardProduct && headCell.value === 'fillingAmount') {
      const value =
        singleStockCardProduct.fillingAmount ??
        singleStockCardProduct.product?.fillingAmount ??
        // @ts-ignore
        stockCard[headCell.value]
      const unit = singleStockCardProduct.fillingUnit ?? ''
      if (value === null || value === undefined || value === '') {
        return { type: 'text', value: '-' }
      }
      return { type: 'text', value: `${value}${unit}` }
    }

    if (isMultiProduct && headCell.value === 'fillingAmount') {
      const vals = stockCard.stockCardProducts.map(
        (p) =>
          `${p.fillingAmount ?? p.product?.fillingAmount ?? ''}${
            p.fillingUnit || ''
          }`
      )
      const unique = Array.from(new Set(vals.filter((v) => v !== '')))
      return {
        type: 'text',
        value: unique.length === 1 ? unique[0] : '(Multiple)',
      }
    }

    if (headCell.dataType === 'date') {
      const value = stockCard[headCell.value]
      if (typeof value === 'string' || typeof value === 'number') {
        return {
          type: 'text',
          value: value ? new Date(value).toDateString() : '-',
        }
      }
      return { type: 'text', value: '-' }
    }
    if (headCell.dataType === 'ingredientLevel') {
      // @ts-ignore
      const value = stockCard.stockCardIngredients?.[0]?.[headCell.value]
      return { type: 'text', value: value ?? '-' }
    }
    if (headCell.dataType === 'ingredientLevelDate') {
      // @ts-ignore
      const value = stockCard.stockCardIngredients?.[0]?.[headCell.value]
      if (typeof value === 'string' || typeof value === 'number') {
        return {
          type: 'text',
          value: value ? new Date(value).toUTCString() : '-',
        }
      }
      return { type: 'text', value: '-' }
    }
    if (headCell.dataType === 'batchLevelData') {
      // @ts-ignore
      const value = stockCard.stockCardBatches?.[0]?.[headCell.value]
      return { type: 'text', value: value ?? '-' }
    }
    if (headCell.dataType === 'productLevelDateData') {
      // @ts-ignore
      const value = stockCard.stockCardProducts?.[0]?.product?.[headCell.value]
      if (typeof value === 'string' || typeof value === 'number') {
        return {
          type: 'text',
          value: value ? new Date(value).toDateString() : '-',
        }
      }
      return { type: 'text', value: '-' }
    }
    if (headCell.dataType === 'stockCardProductLevelData') {
      // @ts-ignore
      const value = stockCard.stockCardProducts?.[0]?.[headCell.value]
      return { type: 'text', value: value ?? '-' }
    }
    if (headCell.dataType === 'integer') {
      return { type: 'text', value: Number(stockCard[headCell.value]) }
    }
    return { type: 'text', value: stockCard[headCell.value] ?? '-' }
  }

  const handlePrintStockCard = () => {
    if (!stockCardData) return
    const columns = headCells[type]
      ?.filter((headCell) => {
        if (inOutDirection === VALUE_IN) return !headCell.isOutOnly
        if (inOutDirection === VALUE_OUT) return !headCell.isInOnly
        return true
      })
      ?.filter((headCell) => headCell.value !== 'pictureUrl')
      .map((headCell) => ({
        label: headCell.label,
        cell: getPrintValue(stockCardData, headCell),
      }))

    const rowsHtml = columns
      .map(({ label, cell }) => {
        if (cell.type === 'image') {
          return `
            <tr>
              <th>${escapeHtml(label)}</th>
              <td>
                ${
                  cell.value
                    ? `<img src="${escapeHtml(
                        cell.value
                      )}" style="max-width:160px;max-height:160px;object-fit:contain;border:1px solid #ccc;border-radius:8px;" />`
                    : '<span style="color:#888;font-style:italic">Tidak ada gambar</span>'
                }
              </td>
            </tr>
          `
        }
        return `
          <tr>
            <th>${escapeHtml(label)}</th>
            <td>${escapeHtml(cell.value)}</td>
          </tr>
        `
      })
      .join('')

    const itemsHtml = (() => {
      let items: any[] = []
      let itemHeaders: string[] = []
      let itemRowRenderer: (item: any) => string = () => ''

      if (stockCardData.stockCardIngredients?.length) {
        items = stockCardData.stockCardIngredients
        itemHeaders = [
          'Kode Ingredient',
          'Nama Ingredient',
          'Qty Diminta',
          'Qty Dikonfirmasi',
        ]
        itemRowRenderer = (ing) => `
          <tr>
            <td>${escapeHtml(
              ing.ingredientId || ing.ingredient?.ingredientId
            )}</td>
            <td>${escapeHtml(
              ing.ingredientName || ing.ingredient?.ingredientName
            )}</td>
            <td>${escapeHtml(ing.quantity)} kg</td>
            <td>${escapeHtml(ing.confirmedQuantity)} kg</td>
          </tr>
        `
      } else if (stockCardData.stockCardPackaging?.length) {
        items = stockCardData.stockCardPackaging
        itemHeaders = [
          'Kode Packaging',
          'Nama Part',
          'Qty Diminta',
          'Qty Dikonfirmasi',
        ]
        itemRowRenderer = (pkg) => `
          <tr>
            <td>${escapeHtml(pkg.packagingPartId)}</td>
            <td>${escapeHtml(pkg.partName)}</td>
            <td>${escapeHtml(pkg.quantity)}</td>
            <td>${escapeHtml(pkg.confirmedQuantity)}</td>
          </tr>
        `
      } else if (stockCardData.stockCardProducts?.length) {
        items = stockCardData.stockCardProducts
        itemHeaders = [
          'Produk',
          'No. Bets',
          'Filling',
          'Qty Diminta',
          'Qty Dikonfirmasi',
        ]
        itemRowRenderer = (prod) => `
          <tr>
            <td>${escapeHtml(
              prod.productName || prod.product?.productName
            )}</td>
            <td>${escapeHtml(prod.batchNumber)}</td>
            <td>${escapeHtml(prod.fillingAmount)}${escapeHtml(
          prod.fillingUnit
        )}</td>
            <td>${escapeHtml(
              Number(prod.quantity) > 0 ? prod.quantity : prod.expectedQuantity
            )}</td>
            <td>${escapeHtml(prod.confirmedQuantity)}</td>
          </tr>
        `
      } else if (stockCardData.stockCardBatches?.length) {
        items = stockCardData.stockCardBatches
        itemHeaders = ['No. Bets', 'Qty Diminta', 'Qty Dikonfirmasi']
        itemRowRenderer = (bat) => `
          <tr>
            <td>${escapeHtml(bat.batchNumber)}</td>
            <td>${escapeHtml(bat.quantity)}</td>
            <td>${escapeHtml(bat.confirmedQuantity)}</td>
          </tr>
        `
      }

      if (items.length === 0) return ''

      return `
        <h3 style="margin-top: 24px;">Detail Item</h3>
        <table>
          <thead>
            <tr>
              ${itemHeaders.map((h) => `<th>${escapeHtml(h)}</th>`).join('')}
            </tr>
          </thead>
          <tbody>
            ${items.map(itemRowRenderer).join('')}
          </tbody>
        </table>
      `
    })()

    const html = `
      <html>
        <head>
          <title>Stock Card ${escapeHtml(
            stockCardData.stockCardDisplayId
          )}</title>
          <style>
            @page { size: auto; margin: 10mm; }
            body { font-family: Arial, sans-serif; padding: 24px; color: #333; }
            h2, h3 { color: #1a73e8; margin-bottom: 16px; }
            table { border-collapse: collapse; width: 100%; margin-bottom: 20px; }
            th, td { border: 1px solid #ddd; padding: 12px 8px; vertical-align: top; text-align: left; }
            th { background: #f8f9fa; font-weight: bold; width: 30%; }
            thead th { background: #e8f0fe; width: auto; }
            .summary th { width: 30%; }
          </style>
        </head>
        <body>
          <h2>Konfirmasi Kartu Stok ${
            inOutDirection === VALUE_IN ? 'Masuk' : 'Keluar'
          }</h2>
          <table class="summary">
            ${rowsHtml}
          </table>
          ${itemsHtml}
        </body>
      </html>
    `

    const printWindow = window.open('', '_blank', 'width=900,height=650')
    if (!printWindow) return
    printWindow.document.write(html)
    printWindow.document.close()
    printWindow.focus()

    const triggerPrint = () => {
      try {
        printWindow.print()
      } catch {
        // no-op
      }
    }

    const waitForImages = () => {
      const images = Array.from(
        printWindow.document.images || []
      ) as HTMLImageElement[]
      if (images.length === 0) {
        triggerPrint()
        return
      }

      let loaded = 0
      const onDone = () => {
        loaded += 1
        if (loaded >= images.length) {
          triggerPrint()
        }
      }

      images.forEach((img) => {
        if (img.complete) {
          onDone()
        } else {
          img.addEventListener('load', onDone)
          img.addEventListener('error', onDone)
        }
      })
    }

    // Wait for window to finish loading its content before checking images
    printWindow.addEventListener('load', waitForImages)
  }

  const getPackagingValidationMessage = (
    stockCard: IStockCard
  ): string | null => {
    if (isSuperuser) return null
    const invalidItem = stockCard.stockCardPackaging.find((packaging) => {
      if (!isValidNumberString(packaging.confirmedQuantity)) return true
      if (Number(packaging.confirmedQuantity) === 0) return true

      if (stockCard.inOutDirection === VALUE_IN) {
        return false
      }
      const matchingPackagingPartStock =
        (packagingParts || []).find(
          (packagingPart) =>
            packagingPart?.packagingPartId &&
            packagingPart.packagingPartId.toLowerCase() ===
              packaging?.packagingPartId?.toLowerCase()
        )?.stock ?? 0
      if (
        Number(packaging.confirmedQuantity) > Number(matchingPackagingPartStock)
      ) {
        return true
      }
      return false
    })

    if (invalidItem) {
      if (!isValidNumberString(invalidItem.confirmedQuantity))
        return 'Jumlah harus berupa angka'
      if (Number(invalidItem.confirmedQuantity) === 0)
        return 'Jumlah tidak boleh 0'
      return 'Jumlah tidak boleh lebih dari jumlah stok sekarang'
    }
    return null
  }

  const getIngredientValidationMessage = (
    stockCard: IStockCard
  ): string | null => {
    if (isSuperuser) return null
    const invalidItem = stockCard.stockCardIngredients.find(
      (stockCardIngredient) => {
        if (!isValidNumberString(stockCardIngredient.confirmedQuantity))
          return true
        if (Number(stockCardIngredient.confirmedQuantity) === 0) return true
        if (stockCard.inOutDirection === VALUE_IN) return false

        const normalizedIngredientId =
          stockCardIngredient.ingredientId ||
          stockCardIngredient.ingredient?.ingredientId
        const matchingIngredientStock =
          ingredients.find(
            (ingredient) =>
              ingredient.ingredientId?.toLowerCase() ===
              normalizedIngredientId?.toLowerCase()
          )?.stock ?? 0
        if (
          Number(stockCardIngredient.confirmedQuantity) >
          Number(matchingIngredientStock)
        ) {
          return true
        }
        return false
      }
    )

    if (invalidItem) {
      if (!isValidNumberString(invalidItem.confirmedQuantity))
        return 'Jumlah harus berupa angka'
      if (Number(invalidItem.confirmedQuantity) === 0)
        return 'Jumlah tidak boleh 0'
      return 'Jumlah tidak boleh lebih dari jumlah stok sekarang'
    }
    return null
  }

  const getProductValidationMessage = (
    stockCard: IStockCard
  ): string | null => {
    if (isSuperuser) return null
    const invalidItem = stockCard.stockCardProducts.find((stockCardProduct) => {
      if (!isValidNumberString(stockCardProduct.confirmedQuantity)) return true
      if (Number(stockCardProduct.confirmedQuantity) === 0) return true

      if (stockCard.inOutDirection === VALUE_IN) return false

      const matchingStock =
        latestStock[stockCard.stockCardProducts.indexOf(stockCardProduct)] ?? 0

      if (Number(stockCardProduct.confirmedQuantity) > Number(matchingStock)) {
        return true
      }
      return false
    })

    if (invalidItem) {
      if (!isValidNumberString(invalidItem.confirmedQuantity))
        return 'Jumlah harus berupa angka'
      if (Number(invalidItem.confirmedQuantity) === 0)
        return 'Jumlah tidak boleh 0'
      return 'Jumlah tidak boleh lebih dari jumlah stok sekarang'
    }
    return null
  }

  const getBatchValidationMessage = (stockCard: IStockCard): string | null => {
    if (isSuperuser) return null
    if (isBatchLoading) return null
    if (stockCard.inOutDirection === VALUE_IN) {
      const invalidItem = stockCardData.stockCardBatches.find((batch) => {
        return (
          !isValidNumberString(batch.confirmedQuantity) ||
          Number(batch.confirmedQuantity) <= 0
        )
      })
      if (invalidItem) {
        if (!isValidNumberString(invalidItem.confirmedQuantity))
          return 'Jumlah harus berupa angka'
        return 'Jumlah harus lebih dari 0'
      }
      return null
    }

    const invalidItem = stockCardData.stockCardBatches.find(
      (stockCardBatch) => {
        if (!isValidNumberString(stockCardBatch.confirmedQuantity)) return true
        if (Number(stockCardBatch.confirmedQuantity) === 0) return true
        const left = Number(stockCardBatch.confirmedQuantity ?? 0)
        const right = Number(
          getMatchingBatch(stockCardBatch)?.currentQuantity ?? 0
        )
        return left > right
      }
    )

    if (invalidItem) {
      if (!isValidNumberString(invalidItem.confirmedQuantity))
        return 'Jumlah harus berupa angka'
      if (Number(invalidItem.confirmedQuantity) === 0)
        return 'Jumlah tidak boleh 0'
      return 'Jumlah tidak boleh lebih dari jumlah stok sekarang'
    }
    return null
  }

  const prefillAllIngredientsByRequestAmount = () => {
    setStockCardData({
      ...stockCardData,
      // @ts-ignore
      stockCardIngredients: stockCardData.stockCardIngredients.map(
        (stockCardIngredient) => ({
          ...stockCardIngredient,
          confirmedQuantity: String(stockCardIngredient.quantity),
        })
      ),
    })
  }

  const prefillAllProductsByRequestAmount = () => {
    setStockCardData({
      ...stockCardData,
      stockCardProducts: stockCardData.stockCardProducts.map((p) => ({
        ...p,
        confirmedQuantity: String(
          Number(p.quantity) > 0 ? p.quantity : p.expectedQuantity
        ),
      })),
    })
  }

  const prefillAllPackagingByRequestAmount = () => {
    setStockCardData({
      ...stockCardData,
      stockCardPackaging: stockCardData.stockCardPackaging.map((p) => ({
        ...p,
        confirmedQuantity: String(p.quantity),
      })),
    })
  }

  const prefillAllBatchesByRequestAmount = () => {
    setStockCardData({
      ...stockCardData,
      stockCardBatches: stockCardData.stockCardBatches.map((b) => ({
        ...b,
        confirmedQuantity: String(b.quantity),
      })),
    })
  }

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false)

  const handleDeleteStockCard = () => {
    const stockCardId = stockCardData.stockCardId
    if (!stockCardId) return
    setIsLoading(true)
    API.del('products', `/premix/deleteStockCard/${stockCardId}`, {})
      .then(() => {
        setAlertMessage('Berhasil menghapus kartu stok.')
        setIsSuccessSnackbarOpen(true)
        setStockCardSelected(false)
        setIsDeleteDialogOpen(false)
        loadStockCard()
      })
      .catch((error) => {
        console.error(error)
        setAlertMessage('Gagal menghapus kartu stok.')
        setIsFailureSnackbarOpen(true)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const filteredStockCards = React.useMemo(() => {
    return stockCards.filter((sc) => {
      // 1. Filter by ID Produk
      if (searchProductId.trim() !== '') {
        const query = searchProductId.toLowerCase().trim()
        const matchSingle = sc.productId?.toString().toLowerCase().includes(query)
        const matchMultiple = sc.stockCardProducts?.some((p) =>
          (p.productId?.toString() || p.product?.productId?.toString() || '')
            .toLowerCase()
            .includes(query)
        )
        if (!matchSingle && !matchMultiple) return false
      }

      // 2. Filter by No. Dokumen
      if (searchDocNum.trim() !== '') {
        const query = searchDocNum.toLowerCase().trim()
        if (!String(sc.stockCardDisplayId || '').toLowerCase().includes(query)) {
          return false
        }
      }

      // 3. Filter by No. Invoice
      if (searchInvoiceNum.trim() !== '') {
        const query = searchInvoiceNum.toLowerCase().trim()
        if (!String(sc.invoiceDisplayId || '').toLowerCase().includes(query)) {
          return false
        }
      }

      // 4. Filter by No. SO
      if (searchSoNum.trim() !== '') {
        const query = searchSoNum.toLowerCase().trim()
        if (!String(sc.salesOrderDisplayId || '').toLowerCase().includes(query)) {
          return false
        }
      }

      return true
    })
  }, [stockCards, searchProductId, searchDocNum, searchInvoiceNum, searchSoNum])

  return (
    <Box>
      <Box p={2} display="flex" gap={2} flexWrap="wrap" alignItems="center">
        <ToggleButtonGroup
          value={filterType}
          exclusive
          onChange={handleFilterChange}
          aria-label="Filter status"
          size="small"
        >
          <ToggleButton value="unconfirmed" aria-label="unconfirmed">
            Belum Dikonfirmasi
          </ToggleButton>
          <ToggleButton value="confirmed" aria-label="confirmed">
            Sudah Dikonfirmasi
          </ToggleButton>
          <ToggleButton value="all" aria-label="all">
            Semua
          </ToggleButton>
        </ToggleButtonGroup>

        <TextField
          label="Cari ID Produk"
          variant="outlined"
          size="small"
          value={searchProductId}
          onChange={(e) => {
            setSearchProductId(e.target.value)
            setPage(0)
          }}
        />
        <TextField
          label="Cari No. Dokumen"
          variant="outlined"
          size="small"
          value={searchDocNum}
          onChange={(e) => {
            setSearchDocNum(e.target.value)
            setPage(0)
          }}
        />
        <TextField
          label="Cari No. Invoice"
          variant="outlined"
          size="small"
          value={searchInvoiceNum}
          onChange={(e) => {
            setSearchInvoiceNum(e.target.value)
            setPage(0)
          }}
        />
        <TextField
          label="Cari No. SO"
          variant="outlined"
          size="small"
          value={searchSoNum}
          onChange={(e) => {
            setSearchSoNum(e.target.value)
            setPage(0)
          }}
        />
      </Box>
      <Box>
        <Box mt={1} />
        {isLoading && (
          <Box p={3}>
            <LoadingIndicator gifSrc={loadingGif} alt="loading" />
          </Box>
        )}
        <Paper>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
          <TableContainer>
            <TableHead>
              <TableRow hover>
                {headCells[type]
                  ?.filter((headCell) => {
                    if (inOutDirection === VALUE_IN) {
                      return !headCell.isOutOnly
                    }
                    if (inOutDirection === VALUE_OUT) {
                      return !headCell.isInOnly
                    }
                    return true
                  })
                  ?.map((headCell) => (
                    <TableCell>{headCell.label}</TableCell>
                  ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredStockCards.map((stockCard) => (
                <TableRow
                  hover
                  onClick={() =>
                    handleStockCardSelection(stockCard.stockCardId ?? 0)
                  }
                  role="checkbox"
                  key={stockCard.stockCardId}
                  className={classes.productRow}
                >
                  {headCells[type]
                    ?.filter((headCell) => {
                      if (inOutDirection === VALUE_IN) {
                        return !headCell.isOutOnly
                      }
                      if (inOutDirection === VALUE_OUT) {
                        return !headCell.isInOnly
                      }
                      return true
                    })
                    ?.map((headCell) => (
                      <TableCell>{mapData(stockCard, headCell)}</TableCell>
                    ))}
                </TableRow>
              ))}
            </TableBody>
          </TableContainer>
        </Paper>
        <Dialog
          fullWidth
          maxWidth="xl"
          open={stockCardSelected}
          onClose={handleDialogClose}
        >
          <Box display="flex" justifyContent="space-between" p={2}>
            <Typography variant="h5">Detail Kartu Stok</Typography>
            <Box display="flex" gap={12}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handlePrintStockCard}
              >
                Print
              </Button>
              <Button variant="outlined" onClick={handleDialogClose}>
                Exit
              </Button>
            </Box>
          </Box>
          <List>
            {!(
              type === VALUE_BARANG_JADI &&
              inOutDirection === VALUE_OUT &&
              !!stockCardData.invoiceId
            ) && (
            <ListItem>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  {headCells[type]
                    ?.filter((headCell) => {
                      const itemLevelFields = [
                        'brandId',
                        'productId',
                        'productName',
                        'premixId',
                        'batchNumber',
                        'fillingAmount',
                        'packagingId',
                        'packagingName',
                        'quantity',
                        'latestStock',
                        'pictureUrl',
                        'labelId',
                      ]
                      if (itemLevelFields.includes(headCell.value)) return false

                      if (inOutDirection === VALUE_IN)
                        return !headCell.isOutOnly
                      if (inOutDirection === VALUE_OUT)
                        return !headCell.isInOnly
                      return true
                    })
                    ?.map((headCell, index) => (
                      <tr key={index}>
                        <th
                          style={{
                            textAlign: 'left',
                            padding: '8px',
                            borderBottom: '1px solid #eee',
                            width: '30%',
                            fontSize: '0.95rem',
                          }}
                        >
                          {headCell.label}
                        </th>
                        <td
                          style={{
                            padding: '8px',
                            borderBottom: '1px solid #eee',
                            fontSize: '1rem',
                          }}
                        >
                          {isEditing &&
                          [
                            'notes',
                            'outgoingDestination',
                            'salesOrderDisplayId',
                            'purchaseOrderNumber',
                            'batchNumber',
                            'quantity',
                            'premixId',
                          ].includes(headCell.value) &&
                          !(
                            headCell.value === 'quantity' &&
                            headCell.dataType === 'batchLevelData'
                          ) ? (
                            <TextField
                              fullWidth
                              value={stockCardData[headCell.value] ?? ''}
                              onChange={(e) =>
                                setStockCardData({
                                  ...stockCardData,
                                  [headCell.value]: e.target.value,
                                })
                              }
                            />
                          ) : (
                            mapData(stockCardData, headCell)
                          )}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </ListItem>
            )}
            {type === VALUE_GUDANG_PACKAGING && (
              <PackagingDialog
                stockCardData={stockCardData}
                inOutDirection={inOutDirection}
                packagingParts={packagingParts}
                onChange={handleStockCardPackagingFieldChange}
                onPrefill={prefillAllPackagingByRequestAmount}
                onConfirm={confirmPackaging}
                isLoading={isLoading}
                getValidationMessage={getPackagingValidationMessage}
                isSuperuser={isSuperuser}
                onUpdate={updateStockCardOnServer}
                isUpdating={isUpdating}
                onDelete={() => setIsDeleteDialogOpen(true)}
                onAddPackaging={handleAddPackaging}
                onRemovePackaging={handleRemovePackaging}
                isEditing={isEditing}
                setIsEditing={setIsEditing}
              />
            )}

            {[VALUE_GUDANG_MIXING, VALUE_GUDANG_RUAHAN].includes(type) && (
              <BatchDialog
                stockCardData={stockCardData}
                inOutDirection={inOutDirection}
                getMatchingBatch={getMatchingBatch}
                onChange={handleStockCardBatchFieldChange}
                onPrefill={prefillAllBatchesByRequestAmount}
                onConfirm={confirmBatch}
                isLoading={isLoading}
                isBatchLoading={isBatchLoading}
                getValidationMessage={getBatchValidationMessage}
                onAddBatch={handleAddBatch}
                onRemoveBatch={handleRemoveBatch}
                allowAddBatch={stockCardData.stockCardBatches.length === 0}
                isSuperuser={isSuperuser}
                onUpdate={updateStockCardOnServer}
                isUpdating={isUpdating}
                onDelete={() => setIsDeleteDialogOpen(true)}
                isEditing={isEditing}
                setIsEditing={setIsEditing}
              />
            )}

            {type === VALUE_BARANG_JADI &&
              inOutDirection === VALUE_OUT &&
              !!stockCardData.invoiceId && (
                <InvoiceOutgoingDialog
                  stockCardData={stockCardData}
                  department={department as Department | undefined}
                  onConfirmed={() => {
                    setAlertMessage('Berhasil menandai stok dikeluarkan.')
                    setIsSuccessSnackbarOpen(true)
                    setStockCardSelected(false)
                    loadStockCard()
                  }}
                  onError={(msg) => {
                    setAlertMessage(msg)
                    setIsFailureSnackbarOpen(true)
                  }}
                />
              )}

            {[
              VALUE_SETENGAH_JADI,
              VALUE_GUDANG_SETENGAH_JADI,
              VALUE_GUDANG_JADI,
              VALUE_BARANG_JADI,
            ].includes(type) &&
              !(
                type === VALUE_BARANG_JADI &&
                inOutDirection === VALUE_OUT &&
                !!stockCardData.invoiceId
              ) && (
              <HalfCompleteDialog
                stockCardData={stockCardData}
                inOutDirection={inOutDirection}
                latestStock={latestStock}
                isBatchLoading={isBatchLoading}
                onChange={handleStockCardFieldChange}
                onPrefill={prefillAllProductsByRequestAmount}
                onConfirm={confirmBarangSetengahJadi}
                isLoading={isLoading}
                getValidationMessage={getProductValidationMessage}
                isSuperuser={isSuperuser}
                onUpdate={updateStockCardOnServer}
                isUpdating={isUpdating}
                onDelete={() => setIsDeleteDialogOpen(true)}
                onAddProduct={handleAddProduct}
                onRemoveProduct={handleRemoveProduct}
                isEditing={isEditing}
                setIsEditing={setIsEditing}
                createdAtLabel={
                  [VALUE_SETENGAH_JADI, VALUE_GUDANG_SETENGAH_JADI].includes(
                    type
                  )
                    ? 'Tanggal Permintaan Dibuat'
                    : 'Tanggal Labelling Selesai'
                }
                autoFormatQuantity={isKosmetikComplete}
                department={department as Department | undefined}
                getProductBatchOptions={
                  [
                    STOCK_CARD_TYPE_COMPLETE,
                    STOCK_CARD_TYPE_HALF_COMPLETE,
                  ].includes(stockCardData.type) && inOutDirection === VALUE_OUT
                    ? getProductBatchOptions
                    : undefined
                }
              />
            )}

            {type === VALUE_GUDANG_BAHAN_BAKU && (
              <IngredientsDialog
                stockCardData={stockCardData}
                inOutDirection={inOutDirection}
                ingredients={ingredients}
                onChange={handleStockCardIngredientFieldChange}
                onPrefill={prefillAllIngredientsByRequestAmount}
                onConfirm={confirmStockCardIngredient}
                isLoading={isLoading}
                getValidationMessage={getIngredientValidationMessage}
                onAddIngredient={handleAddIngredient}
                onRemoveIngredient={handleRemoveIngredient}
                qtyLabelIn={props.qtyLabelIn}
                qtyLabelOut={props.qtyLabelOut}
                isSuperuser={isSuperuser}
                onUpdate={updateStockCardOnServer}
                isUpdating={isUpdating}
                onDelete={() => setIsDeleteDialogOpen(true)}
                isEditing={isEditing}
                setIsEditing={setIsEditing}
              />
            )}
          </List>
        </Dialog>
        <Dialog
          open={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
        >
          <Box p={3}>
            <Typography variant="h6" gutterBottom>
              Konfirmasi Hapus
            </Typography>
            <Typography variant="body1" gutterBottom>
              Apakah Anda yakin ingin menghapus kartu stok ini? Tindakan ini
              tidak dapat dibatalkan.
            </Typography>
            <Box display="flex" justifyContent="flex-end" mt={2} gap={8}>
              <Button
                variant="outlined"
                onClick={() => setIsDeleteDialogOpen(false)}
              >
                Batal
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleDeleteStockCard}
                disabled={isLoading}
              >
                Hapus
              </Button>
            </Box>
          </Box>
        </Dialog>
        <Snackbar
          open={isSuccessSnackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
        >
          <MuiAlert severity="success" elevation={6} variant="filled">
            {alertMessage}
          </MuiAlert>
        </Snackbar>
        <Snackbar
          open={isFailureSnackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
        >
          <MuiAlert severity="error" elevation={6} variant="filled">
            {alertMessage}
          </MuiAlert>
        </Snackbar>
        <Snackbar
          open={isWarningSnackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
        >
          <MuiAlert severity="warning" elevation={6} variant="filled">
            {alertMessage}
          </MuiAlert>
        </Snackbar>
      </Box>
    </Box>
  )
}
